<template>
  <div class="entity-list-page">
    <div class="entity-list-page__header">
      <title-vue title="Parceiros" />
    </div>

    <v-skeleton-loader
      v-if="loading"
      tile
      loading
      class="mx-auto"
      type="card"
      boilerplate
    />

    <v-card v-else class="entity-list-page__card">
      <v-card-text class="pb-0">
        <status-filter-chips
          :value="statusFiltered"
          :options="statusOptions"
          @input="onStatusChange"
        />
      </v-card-text>

      <v-card-title class="pt-4">
        <v-row align="center">
          <v-col cols="12" md="8">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Pesquise pelo nome, documento, email ou telefone"
              hide-details
              dense
              outlined
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              clearable
              dense
              outlined
              hide-details
              :items="franchises"
              v-model="franchiseFiltered"
              item-text="cityName"
              item-value="ibge"
              label="Franquia"
              @change="handleLoadPartners"
            />
          </v-col>
        </v-row>
      </v-card-title>

      <v-data-table
        :header-props="{ sortByText: 'Filtrar por' }"
        :headers="partnerHeaders"
        :items="partners"
        :items-per-page="10"
        :footer-props="{ 'items-per-page-text': 'parceiros por página' }"
        :search="search"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        no-data-text="Nenhum parceiro encontrado"
        class="elevation-0"
      >
        <template v-slot:body="{ items }">
          <tbody>
            <tr v-for="item in items" :key="item.id">
              <td>{{ item.name }}</td>
              <td>{{ formatDocument(item.document) }}</td>
              <td>{{ phoneNumberFormat(item.phone) }}</td>
              <td>{{ item.email }}</td>
              <td>{{ item.address?.cityName || "—" }}</td>
              <td>{{ formatDate(item.createdAt) }}</td>
              <td>
                <span
                  :class="[
                    'premium-badge',
                    partnerPremiumBadgeClass(item.isPremium, item.premiumSource),
                  ]"
                >
                  {{ formatPartnerPremiumLabel(item.isPremium, item.premiumSource) }}
                </span>
              </td>
              <td class="text-center">
                <v-icon size="24" color="#e5183d" @click="handleDetailPartner(item.id)">
                  mdi-chevron-right
                </v-icon>
              </td>
            </tr>
          </tbody>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import TitleVue from "@/components/title/Title.vue";
import StatusFilterChips from "@/components/statusFilter/StatusFilterChips.vue";
import { PARTNER_STATUS_FILTERS } from "@/config/userStatusFilters";
import { FranchiseType } from "@/types/franchise/Franchise";
import { GetAllPartners } from "@/services/partners/GetAllPartners";
import { GetAllFranchises } from "@/services/franchises/GetAllFranchises";
import { PartnerType } from "@/types/partner/Partner";
import {
  formatPartnerPremiumLabel,
  partnerPremiumBadgeClass,
} from "@/utils/partnerPremium";
import { format } from "date-fns";

export default defineComponent({
  components: { TitleVue, StatusFilterChips },

  data() {
    return {
      loading: true,
      partners: [] as PartnerType[],
      franchises: [] as FranchiseType[],
      search: "",
      sortBy: "createdAt",
      sortDesc: true,
      statusFiltered: "PENDING",
      franchiseFiltered: undefined as number | undefined,
      statusOptions: PARTNER_STATUS_FILTERS,
      partnerHeaders: [
        { text: "Nome", value: "name" },
        { text: "Documento", value: "document" },
        { text: "Telefone", value: "phone" },
        { text: "Email", value: "email" },
        { text: "Cidade", value: "cityName" },
        { text: "Cadastro", value: "createdAt" },
        { text: "Premium", value: "isPremium" },
        { text: "", value: "actions", sortable: false, width: "48px" },
      ],
    };
  },

  watch: {
    "$route.query.status"(status: string | undefined) {
      if (status && status !== this.statusFiltered) {
        this.statusFiltered = status;
        void this.handleLoadPartners();
      }
    },
  },

  methods: {
    formatPartnerPremiumLabel,
    partnerPremiumBadgeClass,
    onStatusChange(status: string) {
      this.statusFiltered = status;
      this.$router.replace({ query: { status } }).catch(() => undefined);
      void this.handleLoadPartners();
    },
    async handleLoadFranchises() {
      const getAllFranchises = new GetAllFranchises();
      this.franchises = await getAllFranchises.execute();
    },
    async handleLoadPartners() {
      this.loading = true;
      try {
        const getAllPartners = new GetAllPartners();
        this.partners = await getAllPartners.execute(
          this.statusFiltered,
          this.franchiseFiltered,
        );
      } finally {
        this.loading = false;
      }
    },
    handleDetailPartner(id: string) {
      this.$router.push({
        path: `/partner/${id}`,
        query: { status: this.statusFiltered },
      });
    },
    phoneNumberFormat(value: string): string {
      if (!value) return "—";
      const cleaned = String(value).replace(/\D/g, "");
      if (cleaned.length === 11) {
        return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
      }
      return value;
    },
    formatDate(date: string): string {
      if (!date) return "—";
      return format(new Date(date), "dd/MM/yyyy HH:mm");
    },
    formatDocument(document: string): string {
      if (!document || document.startsWith("P")) return "—";
      if (document.length === 11) {
        return document.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      }
      if (document.length === 14) {
        return document.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
      }
      return document;
    },
  },

  async mounted() {
    const queryStatus = this.$route.query.status;
    if (typeof queryStatus === "string" && queryStatus.length > 0) {
      this.statusFiltered = queryStatus;
    }
    await this.handleLoadFranchises();
    await this.handleLoadPartners();
  },
});
</script>

<style scoped lang="scss">
.entity-list-page__header {
  margin-bottom: 20px;
}

tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.03);
}

.premium-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 4px;
  font-weight: 500;
  color: white;
  white-space: nowrap;

  &--premium {
    background-color: #2e7d32;
  }

  &--voucher {
    background-color: #1565c0;
  }

  &--free {
    background-color: #c62828;
  }
}
</style>
